import {
  Key,
  LogOut,
  Mail,
  Menu,
  Moon,
  Settings,
  ShieldCheck,
  Sun,
  User as UserIcon,
  Users,
  Webhook,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { signOut, useSession } from '../lib/auth-client';
import { useTheme } from '../lib/theme';
import { cn } from '../lib/utils';
import { Badge } from './ui/Badge';

// ─── Nav items ──────────────────────────────────────────────────────────────

interface NavItem {
  id: string;
  label: string;
  icon: typeof Mail;
  to: string;
  adminOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'inbox', label: 'Mail', icon: Mail, to: '/inbox' },
  { id: 'account', label: 'Account', icon: UserIcon, to: '/account' },
  { id: 'api-keys', label: 'API Keys', icon: Key, to: '/account/api-keys' },
  { id: 'webhooks', label: 'Webhooks', icon: Webhook, to: '/account/webhooks' },
  { id: 'admin', label: 'Admin', icon: Users, to: '/admin', adminOnly: true },
];

// ─── Tier badge ──────────────────────────────────────────────────────────────

type Tier = 'free' | 'pro' | 'admin';

function tierBadgeColor(tier: Tier) {
  switch (tier) {
    case 'pro':
      return 'emerald';
    case 'admin':
      return 'rose';
    case 'free':
    default:
      return 'cyan';
  }
}

function tierLabel(is_admin?: boolean, tier?: string | null): { label: string; tier: Tier } {
  if (is_admin) return { label: 'Admin', tier: 'admin' };
  if (tier === 'pro') return { label: 'Pro', tier: 'pro' };
  return { label: 'Free', tier: 'free' };
}

// ─── Layout ──────────────────────────────────────────────────────────────────

interface LayoutProps {
  children: React.ReactNode;
}

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all',
    isActive
      ? 'bg-cyan-50 dark:bg-cyan-500/10 text-cyan-600 dark:text-cyan-400'
      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800',
  );

const mobileNavLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all',
    isActive
      ? 'bg-cyan-50 dark:bg-cyan-500/10 text-cyan-600 dark:text-cyan-400'
      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800',
  );

export function Layout({ children }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, toggle: toggleTheme } = useTheme();
  const { data: session } = useSession();
  const navigate = useNavigate();

  type SessionUser = {
    id: string;
    name: string;
    email: string;
    is_admin?: boolean;
    tier?: string | null;
    [key: string]: unknown;
  };
  const user = (session?.user as SessionUser | undefined) ?? null;

  const filteredNavItems = NAV_ITEMS.filter(
    (item) => !item.adminOnly || user?.is_admin,
  );

  const { label: tierLabelStr, tier } = tierLabel(
    user?.is_admin,
    user?.tier,
  );

  const displayName = user
    ? (user.name || user.email?.split('@')[0] || 'User')
    : null;

  const handleLogout = async () => {
    await signOut();
    setMobileMenuOpen(false);
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950">
      {/* ── Header (fixed) ─────────────────────────────────────── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <NavLink to="/" className="flex items-center gap-2 no-underline">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-sm">
                <ShieldCheck className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-slate-900 dark:text-white tracking-tight">
                AZ Vault
              </span>
            </NavLink>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {filteredNavItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink key={item.id} to={item.to} className={navLinkClass}>
                    <Icon size={18} />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-2">
              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </button>

              {/* Settings link */}
              <button
                onClick={() => navigate('/account')}
                className="p-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                title="Settings"
              >
                <Settings size={18} />
              </button>

              {/* User pill (desktop) */}
              {user && displayName && (
                <div className="hidden md:flex items-center gap-2">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-full">
                    <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center">
                      <UserIcon className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" />
                    </div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      {displayName}
                    </span>
                    <Badge color={tierBadgeColor(tier)} className="uppercase tracking-wide">
                      {tierLabelStr}
                    </Badge>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="p-2 text-slate-500 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-lg transition-colors"
                    title="Logout"
                  >
                    <LogOut size={18} />
                  </button>
                </div>
              )}

              {/* Mobile menu toggle */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
              >
                {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <>
            <div
              className="fixed inset-x-0 top-14 bottom-0 bg-black/50 z-40 md:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
            <div className="absolute top-full left-0 right-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shadow-lg z-50 md:hidden animate-slide-down">
              <nav className="p-3 space-y-1">
                {filteredNavItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.id}
                      to={item.to}
                      className={mobileNavLinkClass}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Icon size={20} />
                      <span className="font-medium">{item.label}</span>
                    </NavLink>
                  );
                })}

                {/* Mobile user info */}
                {user && displayName && (
                  <div className="pt-3 mt-3 border-t border-slate-200 dark:border-slate-800">
                    <div className="flex items-center justify-between px-4 py-2">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                          <UserIcon className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-slate-900 dark:text-white">
                              {displayName}
                            </p>
                            <Badge
                              color={tierBadgeColor(tier)}
                              className="uppercase tracking-wide"
                            >
                              {tierLabelStr}
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-500">{user.email}</p>
                        </div>
                      </div>
                      <button
                        onClick={handleLogout}
                        className="p-2 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-lg transition-colors"
                      >
                        <LogOut size={18} />
                      </button>
                    </div>
                  </div>
                )}
              </nav>
            </div>
          </>
        )}
      </header>

      {/* ── Main content ──────────────────────────────────────── */}
      <main className="w-full max-w-6xl mx-auto px-4 sm:px-6 py-6 pt-20 min-h-[calc(100vh-10rem)]">
        {children}
      </main>

      {/* ── Footer ────────────────────────────────────────────── */}
      <footer className="border-t border-slate-200 dark:border-slate-800 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm mt-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col items-center gap-2 text-center">
            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <ShieldCheck size={14} className="text-cyan-500" />
              <span className="font-medium">AZ Vault</span>
              <span className="text-slate-300 dark:text-slate-600">•</span>
              <span>v0.2.0</span>
            </div>
            <p className="text-xs text-slate-400 dark:text-slate-500">
              Temp mail · 2FA · Passwords — end-to-end encrypted vault on Cloudflare
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
